import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Calendar, CheckCircle, XCircle, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReservationDetailPage = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [status, setStatus] = useState('pending');

  useEffect(() => {
    fetchReservationDetail();
  }, [reservationId]);

  const fetchReservationDetail = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/reservations/${reservationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReservation({
        ...response.data.reservation,
        resource: response.data.resource,
        project: response.data.project,
        from_company: response.data.from_company,
        to_company: response.data.to_company
      });
      setStatus(response.data.reservation?.status || 'pending');
    } catch (error) {
      toast.error('Failed to load reservation details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    const token = localStorage.getItem('token');
    setSaving(true);
    try {
      await axios.put(`${API}/admin/reservations/${reservationId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reservation status updated successfully');
      fetchReservationDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update reservation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/reservations/${reservationId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: 'Deleted from admin panel' }
      });
      toast.success('Reservation deleted successfully');
      navigate('/admin/reservations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete reservation');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleCancel = async () => {
    await handleUpdateStatus();
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status ? status.toUpperCase() : 'N/A'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="p-8">
        <p className="text-red-600">Reservation not found</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/reservations')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Reservations
          </Button>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Reservation Details</h1>
          <p className="text-gray-600 mt-2">View and edit reservation information</p>
        </div>
        <Button variant="outline" onClick={() => setShowDeleteModal(true)} className="text-red-600">
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Reservation
        </Button>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Reservation"
        message="Are you sure you want to delete this reservation?"
        entityName={`Reservation ${reservation.id}`}
        consequences={['Reservation will be marked as deleted']}
        isDeleting={deleting}
      />

      {/* Edit Status Form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Update Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label>Status</Label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border rounded-md px-3 py-2 mt-1"
              >
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <Button onClick={handleUpdateStatus} disabled={saving} style={{ backgroundColor: '#62D84E' }}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Update Status'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reservation Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Reservation Info
              </span>
              {getStatusBadge(reservation.status)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-gray-600 text-sm">Reservation ID</p>
              <p className="font-mono text-sm bg-gray-100 p-2 rounded">{reservation.id}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Resource</p>
              <p className="font-medium">
                {reservation.resource 
                  ? (reservation.resource.name || `${reservation.resource.first_name || ''} ${reservation.resource.last_name || ''}`.trim()) 
                  : (reservation.resource_name || 'N/A')}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Project</p>
              <p className="font-medium">
                {reservation.project?.title || reservation.project_name || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Start Date</p>
              <p className="font-medium">
                {reservation.start_date || reservation.proposed_start_date 
                  ? new Date(reservation.start_date || reservation.proposed_start_date).toLocaleDateString() 
                  : 'Not set'}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">End Date</p>
              <p className="font-medium">
                {reservation.end_date || reservation.proposed_end_date
                  ? new Date(reservation.end_date || reservation.proposed_end_date).toLocaleDateString() 
                  : 'Not set'}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Created At</p>
              <p className="font-medium">{reservation.created_at ? new Date(reservation.created_at).toLocaleString() : 'N/A'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Company Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-gray-600 text-sm">Resource Manager Company</p>
              <p className="font-medium">{reservation.from_company?.name || reservation.rm_company_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Project Manager Company</p>
              <p className="font-medium">{reservation.to_company?.name || reservation.pm_company_name || 'N/A'}</p>
            </div>
            {(reservation.conversation_thread_id || reservation.thread_id) && (
              <div>
                <p className="text-gray-600 text-sm">Conversation Thread</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/admin/messages/${reservation.conversation_thread_id || reservation.thread_id}`)}
                  className="mt-2"
                >
                  View Conversation
                </Button>
              </div>
            )}
            {reservation.notes && (
              <div>
                <p className="text-gray-600 text-sm">Notes</p>
                <p className="text-sm bg-gray-50 p-3 rounded">{reservation.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Checklist Items (if accepted) */}
      {reservation.status === 'accepted' && reservation.checklist_items && reservation.checklist_items.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Checklist Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {reservation.checklist_items.map((item, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded">
                  {item.completed ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                  )}
                  <span className={item.completed ? 'line-through text-gray-500' : 'text-gray-900'}>
                    {item.title}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReservationDetailPage;